import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// Material UI
import { 
  Button, Grid, IconButton, Typography, Box, Card, 
  Divider, Stack, Avatar, Tooltip,Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';

// Components & Redux
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";
import AddUpdateProject from "./AddUpdateProject";
import ProjectCostsSummary from "./ProjectCostsSummary";
import MatrixDialogEdit from "../ListMatrix/MatrixDialogEdit";
import { MatriceWrapper } from "../../interface/matrice";
import { IProject } from "../../interface/project";
import { getOne } from "../../fetchs/get";
import { fetchPostProtect } from "../../fetchs/post";
import { fetchGetProtect } from "../../fetchs/get";

const FK_STATUT = {
  BROUILLON: 0,
  VALIDE: 1,
  CLOS: 2,
};

const ProjectDetails = ({
  project,
  isModified,
  setIsModified,
  isModifiedMatrice,
  setIsModifiedMatrice,
  updateProject,
  setLotId,
  lotId,
  setOpenModal,
}: {
  project: IProject;
  isModified: boolean;
  setIsModified: Function;
  isModifiedMatrice: boolean;
  setIsModifiedMatrice: Function;
  updateProject: Function;
  setLotId: any;
  lotId: string;
  setOpenModal: Function;
}) => {
  const [matrix, setMatrix] = useState<any>([]);
  const [isSwitched, setIsSwitched] = useState<boolean>(true);
  const [isRotated, setIsRotated] = useState(false);
  const [createdMatriceId, setCreatedMatriceId] = useState<string>("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isClos = Number(project?.syncMetadata?.fkStatut) === FK_STATUT.CLOS;
  const token = useSelector((state: RootState) => state.user.token);

useEffect(() => {
  if (token && project && isModifiedMatrice) {
    const matriceIdToLoad = project.defaultMatrice || createdMatriceId;
    if (!matriceIdToLoad) {
      createDefaultMatriceForProject();
      return;
    }
    getOne(token, "/matrice/id/", matriceIdToLoad, setMatrix, 
    () => dispatch(sessionEnd()), 
    (err: any) => {
      navigate(`/projets/`);
      dispatch(showSnackbar(`Erreur matrice : ` + err, "error"));
    });
  }
}, [isModifiedMatrice, project, token, createdMatriceId]);

  const pushModifiedMatrice = (updatedMatriceData: MatriceWrapper) => {
    const body = { ...updatedMatriceData, name: updatedMatriceData.name.trim() };
    if (token) {
      fetchPostProtect(token, "/matrice/update", JSON.stringify(body)).then((res) => {
        if (res.status === 200) {
          dispatch(showSnackbar("Matrice mise à jour", "success"));
          setIsModifiedMatrice(false);
        } else if (res.status === 401) {
          dispatch(sessionEnd());
        }
      });
    }
  };

  const createDefaultMatriceForProject = async () => {
  if (!token || !project) return;
  try {
    const masterRes = await fetchGetProtect(token, "/matrice/master/", "project_master");
    const masterJson = await masterRes.json();
    const masterMatrice = masterJson.message;

    const createRes = await fetchPostProtect(
      token,
      "/matrice/",
      JSON.stringify({
        name: `Template ${project.name}`,
        desc: `Template de la matrice associé au projet [${project.name}]`,
        data: masterMatrice.data,
        clients: [],
        basedOn: "",
        type: "project",
      })
    );
    const newMatrice = await createRes.json();

    await fetchPostProtect(
      token,
      "/projects/update-default-matrice",
      JSON.stringify({ projectId: project._id, matriceId: newMatrice._id })
    );

    setCreatedMatriceId(newMatrice._id);
    setMatrix(newMatrice);
  } catch (e) {
    dispatch(showSnackbar("Erreur lors de la création de la matrice par défaut du projet", "error"));
  }
};

  const togglePanel = () => {
    setIsSwitched(!isSwitched);
    setIsRotated(!isRotated);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={3} position="relative">
        
        {/* Flèche de bascule (Side Panel) */}
        {!isModified && !isModifiedMatrice && (
          <Tooltip title={isSwitched ? "Masquer détails" : "Afficher détails"}>
            <IconButton 
              onClick={togglePanel}
              sx={{
                position: 'absolute',
                top: 10,
                right: isSwitched ? "26%" : "20px",
                zIndex: 10,
                transition: 'all 0.4s ease',
                transform: isRotated ? 'rotate(90deg)' : 'rotate(270deg)',
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#eff6ff' }
              }}
            >
              <KeyboardArrowDown sx={{ color: '#3b82f6' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* SECTION GAUCHE : RÉSUMÉ DES COÛTS / LOTS */}
        <Grid item xs={12} md={isSwitched ? (isModified || isModifiedMatrice ? 0 : 9) : 12}>
          {!isModified && !isModifiedMatrice && (
            <ProjectCostsSummary 
              project={project} 
              setLotId={setLotId} 
              lotId={lotId} 
              setOpenModal={setOpenModal} 
            />
          )}
        </Grid>

        {/* SECTION DROITE : MÉTHADONNÉES OU ÉDITION */}
        {isSwitched && (
          <Grid item xs={12} md={isModified || isModifiedMatrice ? 12 : 3}>
            <Card
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                height: "100%",
                minWidth: isModified ? "500px" : "auto",
                mx: isModified ? "auto" : 0
              }}
            >
              {isModified ? (
                /* FORMULAIRE ÉDITION PROJET */
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight={700}>Édition du projet</Typography>
                    <IconButton onClick={() => setIsModified(false)} size="small"><CloseIcon /></IconButton>
                  </Stack>
                  <AddUpdateProject project={project} handleSubmit={updateProject} edit={true} />
                </Box>
              ) : isModifiedMatrice ? (
                /* ÉDITION MATRICE */
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight={700}>Template Matrice</Typography>
                    <IconButton onClick={() => setIsModifiedMatrice(false)}><CloseIcon /></IconButton>
                  </Stack>
                  {matrix?.data && (
                    <MatrixDialogEdit 
                      matrice={matrix} 
                      setOpenModal={setIsModifiedMatrice} 
                      pushModifiedMatrice={pushModifiedMatrice} 
                      isMaster={false} 
                      project={project} 
                    />
                  )}
                </Box>
              ) : (
                /* VUE INFOS PROJET */
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" fontWeight={700}>Nom du projet</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" color="primary.main" fontWeight={800}>{project.name}</Typography>
                      {isClos && (
                        <Chip 
                          label="Clos" 
                          size="small" 
                          variant="outlined" 
                          color="default"
                          sx={{ borderRadius: "8px", fontWeight: 600 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {project.description && (
                    <Box>
                      <Typography variant="overline" color="text.secondary" fontWeight={700}>Description</Typography>
                      <Typography variant="body2">{project.description}</Typography>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="overline" color="text.secondary" fontWeight={700}>Client</Typography>
                    </Stack>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                      {project.client.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {project.client.address}<br />
                      {project.client.zip} {project.client.town}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <PersonOutlineIcon fontSize="small" color="action" />
                      <Typography variant="overline" color="text.secondary" fontWeight={700}>Historique</Typography>
                    </Stack>
                    <Typography variant="caption" display="block">
                      <b>Créé par :</b> {project.author}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                      le {project.syncMetadata?.date_c 
                            ? dayjs.unix(project.syncMetadata.date_c).format("DD/MM/YYYY HH:mm")
                            : dayjs(project.createdAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <b>Dernière modif :</b> {project.lastUpdatedBy}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      le {dayjs(project.lastUpdatedDate).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Box>

                  <Stack spacing={1} pt={2}>
                    <Tooltip title="Voir l'historique du projet et ses éléments">
                      <span>
                        <Button
                          fullWidth
                          startIcon={<HistoryIcon />}
                          variant="contained"
                          onClick={() => navigate(`/projet/${project._id}/historique`)}
                          sx={{ 
                            borderRadius: "10px", 
                            py: 1, 
                            boxShadow: 'none',
                            bgcolor: '#3b82f6',
                            '&:hover': { bgcolor: '#2563eb' }
                          }}
                        >
                          Voir l'historique
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title={isClos ? "Le projet est clos, modification impossible depuis Kairos" : ""}>
                      <span>
                        <Button
                          fullWidth
                          startIcon={<EditTwoToneIcon />}
                          variant="outlined"
                          onClick={() => setIsModifiedMatrice(true)}
                          disabled={isClos}
                          sx={{ borderRadius: "10px", py: 1 }}
                        >
                          Template Matrice
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              )}
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ProjectDetails;